import { getStoredCompanyKey, getSessionUserId } from '@/services/api/erpat-client';
import type { Priority } from '@/types/todo';

/**
 * Per-task fields the todos API cannot store, kept locally.
 *
 * `PATCH /v1/api/me/todos/{id}` documents exactly five writable fields —
 * `title`, `description`, `due_date`, `priority`, `completed` — and `POST`
 * adds only `tags`. Everything else the task editor and Focus Mode collect
 * (estimate, collaborators, labels on an EDIT, repeat, checklist, focus state)
 * has nowhere to go on the server, so it is held here and merged back in
 * `toTask()`. Without this the editor reports "Save changes" and then loses the
 * change on the next refresh.
 *
 * Same two limits as todo-planned-today.ts, both inherent to being client-only:
 *  - Per device. The same edit made on a second device isn't reflected here.
 *  - Invisible to ERPat's own web/other clients — this never leaves the device.
 *
 * Scoped by company key AND user id, and cleared at sign-out. Task ids are
 * small integers that repeat across tenants and users, so a single global key
 * let one person's task 312 inherit another's priority, estimate and
 * COLLABORATOR NAMES — wrong data, and a disclosure of names from a session
 * that had ended.
 */

const STORAGE_PREFIX = 'syntry_todo_local_fields:';

/**
 * The single global key this store replaced. It holds exactly the data the
 * scoping above exists to prevent — one pooled map of task fields, including
 * collaborator names, shared by every tenant and user who ever signed in on
 * this device — so it is purged rather than migrated.
 */
const LEGACY_GLOBAL_KEY = 'syntry.todo.local-fields.v1';

let legacyPurged = false;

function purgeLegacyKey(): void {
  if (legacyPurged || typeof window === 'undefined') return;
  legacyPurged = true;
  try {
    window.localStorage.removeItem(LEGACY_GLOBAL_KEY);
  } catch {
    /* private mode / storage disabled — nothing to purge */
  }
}

export interface TodoLocalFields {
  estimateMinutes?: number;
  collaborators?: string[];
  priority?: Priority;
  /** Labels — documented on POST but not on PATCH, so edits live here. */
  labels?: string[];
  /** Repeat rule, as one of the editor's `RepeatValue` keys. */
  repeat?: string;
  /** Checklist item titles, in order. */
  checklistItems?: string[];
  /**
   * The task is in Focus Mode. There is no `status` field in PATCH's schema, so
   * "in progress" cannot be a server state — see TodoPage's `focusTask`.
   */
  focused?: boolean;
  /**
   * Seconds already banked against this task — ERPat's `focus_time_seconds`.
   * Accumulates across sessions, so re-focusing a task continues its count.
   */
  focusSeconds?: number;
  /**
   * When the CURRENT run began (epoch ms). Absent whenever the task is not
   * actively being timed — that is what "paused" means here.
   */
  focusRunningSince?: number;
}

type FieldsById = Record<string, TodoLocalFields>;

function storageKey(): string {
  purgeLegacyKey();
  const tenant = (getStoredCompanyKey() ?? '').trim().toLowerCase();
  return `${STORAGE_PREFIX}${tenant}:${getSessionUserId()}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readAll(): FieldsById {
  if (typeof window === 'undefined') return {};
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(storageKey()) ?? '{}');
    if (!isRecord(parsed)) return {};
    const out: FieldsById = {};
    for (const [id, fields] of Object.entries(parsed)) {
      if (isRecord(fields)) out[id] = fields as TodoLocalFields;
    }
    return out;
  } catch {
    return {};
  }
}

function writeAll(fields: FieldsById): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(), JSON.stringify(fields));
  } catch {
    /* private mode / quota — the fields just don't persist this session */
  }
}

/** The locally-held fields for one task ('{}' when there are none). */
export function localTaskFields(taskId: string): TodoLocalFields {
  return readAll()[taskId] ?? {};
}

/**
 * Merge `patch` into one task's fields. A key set to `undefined` is REMOVED,
 * which is how a cleared control (no collaborators, no checklist) erases what
 * was stored rather than leaving the previous value behind.
 */
export function mergeLocalTaskFields(taskId: string, patch: TodoLocalFields): void {
  const all = readAll();
  const next: TodoLocalFields = { ...(all[taskId] ?? {}) };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) delete next[key as keyof TodoLocalFields];
    else Object.assign(next, { [key]: value });
  }
  if (Object.keys(next).length === 0) delete all[taskId];
  else all[taskId] = next;
  writeAll(all);
}

/** Forget one task's local fields (the task was deleted). */
export function clearLocalTaskFields(taskId: string): void {
  const all = readAll();
  if (!(taskId in all)) return;
  delete all[taskId];
  writeAll(all);
}

/**
 * The id of the task currently in Focus Mode, or ''.
 *
 * Focus is exclusive — starting one task's timer ends any other — so it reads
 * as a single id rather than a flag per task.
 */
export function focusedTaskId(): string {
  for (const [id, fields] of Object.entries(readAll())) {
    if (fields.focused) return id;
  }
  return '';
}

/**
 * Total focus time on a task: what is banked, plus the run in progress.
 *
 * Modelled on ERPat's web pad — the panel there shows `focusBaseSeconds` plus
 * the live session, so a resumed task continues its count instead of starting
 * over. Measured from a wall-clock instant rather than counted in ticks,
 * because a phone suspends timers the moment the app goes to the background.
 */
export function focusTotalSeconds(taskId: string): number {
  const fields = readAll()[taskId] ?? {};
  const banked = Math.max(0, Number(fields.focusSeconds) || 0);
  const since = Number(fields.focusRunningSince) || 0;
  if (!since) return banked;
  return banked + Math.max(0, Math.floor((Date.now() - since) / 1000));
}

/** Whether a task's timer is running right now (as opposed to paused). */
export function isFocusRunning(taskId: string): boolean {
  return (Number(readAll()[taskId]?.focusRunningSince) || 0) > 0;
}

/** Begin a run on `taskId`, continuing from whatever it has already banked. */
export function startFocusRun(taskId: string): void {
  if (isFocusRunning(taskId)) return;
  mergeLocalTaskFields(taskId, { focusRunningSince: Date.now() });
}

/**
 * Bank the run in progress and stop timing.
 *
 * Called on pause, on completion, and when focus moves to another task — the
 * same three moments ERPat banks at, so no session is ever silently dropped.
 * A no-op when nothing is running, so it is safe to call defensively.
 */
export function bankFocusRun(taskId: string): number | null {
  const fields = readAll()[taskId] ?? {};
  const since = Number(fields.focusRunningSince) || 0;
  if (!since) return null;
  const banked = Math.max(0, Number(fields.focusSeconds) || 0);
  const run = Math.max(0, Math.floor((Date.now() - since) / 1000));
  const total = banked + run;
  mergeLocalTaskFields(taskId, {
    focusSeconds: total,
    focusRunningSince: undefined,
  });
  // Handed back so the caller can push it to the shared bank — see
  // `bankAndPushFocus` in TodoPage.
  return total;
}

/**
 * Take the server's banked total when it is ahead of ours.
 *
 * The server is the shared bank — ERPat's web pad writes it through
 * `todo/save_focus_time`, and other devices write it through the API — so time
 * spent elsewhere arrives here. Ahead-of-ours rather than blindly overwriting:
 * a run banked locally but not yet pushed (offline, or a failed PATCH) must not
 * be thrown away by a stale server number.
 */
export function adoptServerFocusSeconds(taskId: string, serverSeconds: number): void {
  const seconds = Math.max(0, Math.floor(serverSeconds));
  if (seconds <= 0) return;
  const local = Math.max(0, Number(readAll()[taskId]?.focusSeconds) || 0);
  if (seconds <= local) return;
  mergeLocalTaskFields(taskId, { focusSeconds: seconds });
}

/** Forget a task's focus time entirely (it was reopened from completed). */
export function resetFocusTime(taskId: string): void {
  mergeLocalTaskFields(taskId, { focusSeconds: undefined, focusRunningSince: undefined });
}

/** Put `taskId` in focus (''/null clears focus without starting another). */
export function setFocusedTask(taskId: string | null): void {
  const all = readAll();
  let changed = false;
  for (const [id, fields] of Object.entries(all)) {
    if (fields.focused && id !== taskId) {
      delete fields.focused;
      if (Object.keys(fields).length === 0) delete all[id];
      changed = true;
    }
  }
  if (taskId && !all[taskId]?.focused) {
    all[taskId] = { ...(all[taskId] ?? {}), focused: true };
    changed = true;
  }
  if (changed) writeAll(all);
}

/** Drop every locally-held task field for the signed-in user (sign-out). */
export function clearAllLocalTaskFields(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(storageKey());
  } catch {
    /* nothing to clear */
  }
}

/**
 * Which focus session THIS device started.
 *
 * The server's session is a single `focus_started_at` stamp on the task, with
 * no record of the client that set it — so "am I the one working on this?" is a
 * fact about this device and has to be kept here. Without it, the ERPat web
 * pressing Focus makes every other signed-in client mirror the session as its
 * own: the phone flips the task to in progress, hands it the My Day panel and
 * starts a clock the person sitting in front of the web app never asked it to
 * start.
 *
 * Identified by task AND start instant, not task alone. Pausing and resuming
 * the same task in the web writes a fresh `focus_started_at`, and that new
 * session is not this device's — matching on the id alone would let ownership
 * survive a handover to another client.
 */
const OWNED_FOCUS_PREFIX = 'syntry_todo_focus_owner:';

function ownedFocusKey(): string {
  const tenant = (getStoredCompanyKey() ?? '').trim().toLowerCase();
  return `${OWNED_FOCUS_PREFIX}${tenant}:${getSessionUserId()}`;
}

function sessionTag(taskId: string, startedAtUtc: string): string {
  return `${taskId}@${startedAtUtc}`;
}

/**
 * Held in memory as well as in storage, and MEMORY WINS.
 *
 * localStorage is only how ownership survives a reload. If it throws — private
 * mode, storage disabled — the device would otherwise fail to recognise its own
 * running session, and the Pause it offers would start a second one instead of
 * stopping the first. Cached with the key it was read under, so switching
 * tenant or user re-reads rather than inheriting the previous session's answer.
 */
let owned: { key: string; tag: string } | null = null;

/** Record that this device took the shared clock. */
export function rememberOwnedFocus(taskId: string, startedAtUtc: string): void {
  if (typeof window === 'undefined' || !taskId || !startedAtUtc) return;
  const key = ownedFocusKey();
  const tag = sessionTag(taskId, startedAtUtc);
  owned = { key, tag };
  try {
    window.localStorage.setItem(key, tag);
  } catch {
    /* storage disabled — the in-memory copy above still carries this session */
  }
}

/** Whether the running session described by the server is this device's. */
export function ownsFocusSession(taskId: string, startedAtUtc: string): boolean {
  if (typeof window === 'undefined' || !taskId || !startedAtUtc) return false;
  const key = ownedFocusKey();
  if (owned?.key !== key) {
    let stored = '';
    try {
      stored = window.localStorage.getItem(key) ?? '';
    } catch {
      stored = '';
    }
    owned = { key, tag: stored };
  }
  return owned.tag === sessionTag(taskId, startedAtUtc);
}

/** This device no longer holds the clock — it paused, or something else took it. */
export function forgetOwnedFocus(): void {
  if (typeof window === 'undefined') return;
  const key = ownedFocusKey();
  owned = { key, tag: '' };
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nothing to forget */
  }
}

/**
 * Drop the run in progress WITHOUT banking it.
 *
 * The counterpart to `bankFocusRun` for the one case where banking would be
 * wrong: another client took the shared clock, and the server already banked
 * this run's true elapsed when it stopped the session. Adding our own
 * locally-measured copy on top would count those minutes twice.
 */
export function discardFocusRun(taskId: string): void {
  if (!(Number(readAll()[taskId]?.focusRunningSince) || 0)) return;
  mergeLocalTaskFields(taskId, { focusRunningSince: undefined });
}
